import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Task } from '../../../types/task';
import { Project } from '../../../types/project';
import { GroupBase } from './GroupBase';
import { Box, Flex, HStack, Icon, useColorModeValue } from '@chakra-ui/react';
import { FiArrowRight, FiFolder, FiFolderMinus, FiFolderPlus } from 'react-icons/fi';
import { getProjectColor } from '../../../utils/colorUtils'; // 导入颜色工具
// 导入翻译钩子
import { useTranslation } from 'react-i18next';

// --- ProjectGroup Component Definition ---
export const ProjectGroup = ({ 
  project, 
  tasks,
  isOpen, 
  onToggle, 
  onSelectTask, 
  onToggleDone, 
  onDeleteTask,
  onQuickAddTask 
}: { 
  project: Project | null; 
  tasks: Task[];
  isOpen: boolean;
  onToggle: () => void;
  onSelectTask: (task: Task) => void;
  onToggleDone: (task: Task, done: boolean) => void;
  onDeleteTask: (id: string) => void;
  onQuickAddTask: (projectId: string | null, title: string, startAt?: string) => void;
}) => {
  const navigate = useNavigate();
  // 使用翻译钩子
  const { t } = useTranslation(['v0/projects', 'v0/common']);
  
  // 获取项目颜色 - 使用项目标题生成
  const projectHexColor = project ? getProjectColor(project.title) : null; // Get hex color or null
  
  const arrowColor = useColorModeValue('#767676', '#A0A0A0');
  
  const defaultTitleColor = useColorModeValue("gray.700", "gray.200"); // Default title color
  
  // 简单的处理导航到项目详情
  const handleViewProject = () => {
    if (project) {
      navigate(`/v0/projects/${project.id}`);
    }
  };
  
  // 极简而现代的项目标题头部，箭头放在左侧项目标题旁边
  const customHeader = project ? (
    <Flex 
      width="100%" 
      justifyContent="space-between" 
      alignItems="center"
    >
      {/* Use div and Tailwind for the clickable header area */}
      <div className="flex items-center flex-1 space-x-2 cursor-pointer" onClick={onToggle}>
         {/* Use span and inline style for icon color */}
         <span className="h-5 w-5 flex items-center justify-center flex-shrink-0" style={{ color: projectHexColor ?? defaultTitleColor }}>
           {isOpen ? <FiFolderMinus /> : <FiFolderPlus />} 
         </span>
         {/* Use span and inline style for title color */}
         <span 
             className={`font-semibold text-md truncate`}
             style={{ color: projectHexColor ? `${projectHexColor} !important` : defaultTitleColor }}
         >
             {project.title}
         </span>
          
          {/* 箭头放在项目标题旁边 */}
           <div // Change Box to div
            onClick={(e: React.MouseEvent) => {
               e.stopPropagation(); // Prevent toggle when clicking arrow
              handleViewProject();
            }}
             className="flex items-center ml-1.5 p-1 rounded-full transition-all duration-200 hover:translate-x-0.5 cursor-pointer" 
          >
            <Icon 
              as={FiArrowRight} 
              color={arrowColor}
              boxSize="14px"
              opacity={0.7}
              transition="all 0.2s"
              _groupHover={{ 
                opacity: 1,
                transform: 'translateX(1px)'
              }}
            />
           </div>
      </div>

      {/* 右侧显示任务数量，保留原来的空间 */}
      <Box></Box>
    </Flex>
  ) : null;

  // 处理任务快速添加，合并 GroupBase 的选项和 ProjectGroup 的默认 projectId
  const handleProjectGroupQuickAddTask = (title: string, options?: { projectId?: string | null, startAt?: string }) => {
    const defaultProjectId = project?.id || null;
    // Use projectId from GroupBase options if provided, otherwise use the group's default
    const finalProjectId = options?.projectId ?? defaultProjectId;
    const finalStartAt = options?.startAt; // startAt selection comes solely from GroupBase
    
    onQuickAddTask(finalProjectId, title, finalStartAt); // Call the original handler passed from parent
  };

  return (
    <Box 
      role="group"
      position="relative"
      transition="all 0.2s"
    >
      <GroupBase
        title={project ? project.title : t('v0/projects:noProjectTasks', '无分组任务')}
        tasks={tasks}
        isOpen={isOpen}
        onToggle={onToggle}
        onSelectTask={onSelectTask}
        onToggleDone={onToggleDone}
        onDeleteTask={onDeleteTask}
        onQuickAddTask={handleProjectGroupQuickAddTask}
        addButtonProjectId={project?.id || null}
        customHeader={customHeader}
        projectId={project?.id}
        groupType="project"
      />
    </Box>
  );
}; 